import { useState } from 'react'
import {
  Container,
  Breadcrumb,
  Row,
  Col,
  Table,
  Button,
  Card,
  Form,
  InputGroup,
  Spinner,
  Alert,
  Collapse,
  Nav,
  Tab,
} from 'react-bootstrap'
import { Link, useParams, useHistory } from 'react-router-dom'
import useTerminalDetail from 'hooks/use-terminal-detail'
import useAnsibles from 'hooks/use-ansibles'
import useReceivedMacMinis, {
  useReceivedMacMinisCount,
} from 'hooks/use-received-mac-minis'
import useReceivedPhysicalPhones, {
  useReceivedPhysicalPhonesCount,
} from 'hooks/use-received-physical-phones'
import { useDeviceSelection } from 'hooks/use-device-selection'
import useAppleIdConfigs from 'hooks/use-apple-id-configs'
import { Ansible, AnsibleStatus } from 'interfaces/ansible'
import { PnsMacMini } from 'interfaces/pns-mac-mini'
import { PhysicalPhone } from 'interfaces/physical-phone'
import { AddMacMiniForm } from 'components/add-mac-mini-form'
import { AddPhysicalPhoneForm } from 'components/add-physical-phone-form'
import { DeviceAssignmentModal } from 'components/device-assignment'
import { MacMiniAssignmentModal } from 'components/mac-mini-assignment'
import CreateAppleIdModal from 'components/phone-number-management/create-apple-id-modal'
import useDebounce from 'hooks/use-debounce'

const InventoryTerminalDetailPage = () => {
  const { terminalId } = useParams<{ terminalId: string }>()
  const history = useHistory()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page] = useState(1)
  const [showAddMacMiniForm, setShowAddMacMiniForm] = useState(false)
  const [showAddPhoneForm, setShowAddPhoneForm] = useState(false)
  const [activeTab, setActiveTab] = useState('ansibles')
  const [showAssignmentModal, setShowAssignmentModal] = useState(false)
  const [showMacMiniAssignmentModal, setShowMacMiniAssignmentModal] =
    useState(false)
  const [showCreateAppleIdModal, setShowCreateAppleIdModal] = useState(false)

  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  const {
    data: terminal,
    isLoading: terminalLoading,
    error: terminalError,
  } = useTerminalDetail(terminalId)

  const {
    data: ansiblesData,
    isLoading: ansiblesLoading,
    error: ansiblesError,
  } = useAnsibles({
    terminalId,
    page,
    limit: 20,
    name: debouncedSearchTerm, // PNS uses 'name' for search
    status: statusFilter,
  })

  // Received devices queries
  const { data: receivedMacMinisCount } = useReceivedMacMinisCount(terminalId)
  const { data: receivedPhonesCount } =
    useReceivedPhysicalPhonesCount(terminalId)

  const { data: receivedMacMinis, isLoading: macMinisLoading } =
    useReceivedMacMinis({
      terminalId,
      page: 1,
      limit: 50,
    })

  const { data: receivedPhones, isLoading: phonesLoading } =
    useReceivedPhysicalPhones({
      terminalId,
      page: 1,
      limit: 50,
    })

  // Fetch available Apple ID configs (global, not filtered by terminal)
  const { data: appleIdConfigsData, isLoading: appleIdConfigsLoading } =
    useAppleIdConfigs({
      limit: 100,
    })

  // Transform received devices for device selection
  const availableMacMinis =
    receivedMacMinis?.data?.map((macMini) => ({
      id: macMini.id,
      name: macMini.name,
      type: 'mac-mini' as const,
    })) || []

  const availablePhones =
    receivedPhones?.data?.map((phone) => ({
      id: phone.id,
      name: phone.name,
      type: 'physical-phone' as const,
    })) || []

  // Device selection hooks
  const macMiniSelection = useDeviceSelection(availableMacMinis)
  const phoneSelection = useDeviceSelection(availablePhones)

  // Combined selected devices for assignment
  const allSelectedDevices = [
    ...macMiniSelection.selectedDevices,
    ...phoneSelection.selectedDevices,
  ]
  const hasAnySelection =
    macMiniSelection.hasSelection || phoneSelection.hasSelection

  const handleAnsibleClick = (ansible: Ansible) => {
    history.push(`/inventory/terminals/${terminalId}/ansibles/${ansible.id}`)
  }

  const handleAssignSelectedDevices = () => {
    if (hasAnySelection) {
      setShowAssignmentModal(true)
    }
  }

  const handleAssignMacMinis = () => {
    if (macMiniSelection.hasSelection) {
      setShowMacMiniAssignmentModal(true)
    }
  }

  const handleAssignmentSuccess = () => {
    // Clear all selections after successful assignment
    macMiniSelection.selectNone()
    phoneSelection.selectNone()
    setShowAssignmentModal(false)
  }

  const handleMacMiniAssignmentSuccess = () => {
    // Clear Mac Mini selections after successful assignment
    macMiniSelection.selectNone()
    setShowMacMiniAssignmentModal(false)
  }

  const getStatusVariant = (status: AnsibleStatus) => {
    switch (status) {
      case AnsibleStatus.ACTIVE:
        return 'success'
      case AnsibleStatus.MAINTENANCE:
        return 'warning'
      case AnsibleStatus.DECOMMISSIONED:
        return 'danger'
      default:
        return 'secondary'
    }
  }

  if (terminalError || ansiblesError) {
    return (
      <Container>
        <Alert variant="danger">
          Error loading data:{' '}
          {terminalError instanceof Error
            ? terminalError.message
            : ansiblesError instanceof Error
            ? ansiblesError.message
            : 'Unknown error'}
        </Alert>
      </Container>
    )
  }

  return (
    <Container>
      <Breadcrumb>
        <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
        <Breadcrumb.Item href="/inventory">Inventory</Breadcrumb.Item>
        <Breadcrumb.Item active>
          {terminal?.name || 'Terminal Details'}
        </Breadcrumb.Item>
      </Breadcrumb>

      {terminalLoading ? (
        <div className="text-center">
          <Spinner animation="border" />
          <div className="mt-2">Loading terminal details...</div>
        </div>
      ) : (
        <>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1>{terminal?.name}</h1>
            <span
              className={`badge bg-${
                terminal?.status === 'OPERATIONAL'
                  ? 'success'
                  : terminal?.status === 'MAINTENANCE'
                  ? 'warning'
                  : 'danger'
              } fs-6`}
            >
              {terminal?.status}
            </span>
          </div>

          {/* Terminal Information Card */}
          <Card className="mb-4">
            <Card.Header>
              <h4>Terminal Information</h4>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <p>
                    <strong>Name:</strong> {terminal?.name}
                  </p>
                  <p>
                    <strong>Location Code:</strong> {terminal?.locationCode}
                  </p>
                  <p>
                    <strong>Status:</strong> {terminal?.status}
                  </p>
                </Col>
                <Col md={6}>
                  <p>
                    <strong>Total Ansibles:</strong> {terminal?.totalAnsibles}
                  </p>
                  <p>
                    <strong>Active Ansibles:</strong> {terminal?.activeAnsibles}
                  </p>
                  <p>
                    <strong>Address:</strong> {terminal?.address || 'N/A'}
                  </p>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Device Management Section */}
          <Card className="mb-4">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h4>Device Management</h4>
              <div className="d-flex gap-2">
                <span className="badge bg-info">
                  Received Mac Minis: {receivedMacMinisCount || 0}
                </span>
                <span className="badge bg-info">
                  Received Phones: {receivedPhonesCount || 0}
                </span>
              </div>
            </Card.Header>
            <Card.Body>
              <Row className="mb-3">
                <Col md={6}>
                  <div className="d-grid gap-2">
                    <Button
                      variant="outline-success"
                      onClick={() => setShowAddMacMiniForm(!showAddMacMiniForm)}
                    >
                      {showAddMacMiniForm ? '▼ Hide' : '▶ Add Mac Mini'}
                    </Button>
                    <Collapse in={showAddMacMiniForm}>
                      <div>
                        <Card className="mt-2">
                          <Card.Body>
                            <AddMacMiniForm
                              terminalId={terminalId}
                              terminalName={terminal?.name || ''}
                              onSuccess={() => setShowAddMacMiniForm(false)}
                            />
                          </Card.Body>
                        </Card>
                      </div>
                    </Collapse>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="d-grid gap-2">
                    <Button
                      variant="outline-primary"
                      onClick={() => setShowAddPhoneForm(!showAddPhoneForm)}
                    >
                      {showAddPhoneForm ? '▼ Hide' : '▶ Add Physical Phone'}
                    </Button>
                    <Collapse in={showAddPhoneForm}>
                      <div>
                        <Card className="mt-2">
                          <Card.Body>
                            <AddPhysicalPhoneForm
                              terminalId={terminalId}
                              terminalName={terminal?.name || ''}
                              onSuccess={() => setShowAddPhoneForm(false)}
                            />
                          </Card.Body>
                        </Card>
                      </div>
                    </Collapse>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Navigation Tabs */}
          <Tab.Container
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k || 'ansibles')}
          >
            <Nav variant="tabs" className="mb-3">
              <Nav.Item>
                <Nav.Link eventKey="ansibles">
                  Ansibles (
                  {ansiblesLoading
                    ? '...'
                    : ansiblesData?.pagination?.total ?? 0}
                  )
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="received-mac-minis">
                  Received Mac Minis ({receivedMacMinisCount ?? 0})
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="received-phones">
                  Received Phones ({receivedPhonesCount ?? 0})
                </Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="apple-id-configs">
                  Apple IDs (
                  {appleIdConfigsLoading
                    ? '...'
                    : appleIdConfigsData?.meta?.total ?? 0}
                  )
                </Nav.Link>
              </Nav.Item>
            </Nav>

            <Tab.Content>
              {/* Ansibles Tab */}
              <Tab.Pane eventKey="ansibles">
                {/* Ansibles Table */}
                <Card className="mb-4">
                  <Card.Header>
                    <h4>Ansibles in {terminal?.name}</h4>
                  </Card.Header>
                  <Card.Body>
                    <Row className="mb-3">
                      <Col md={6}>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            placeholder="Search ansibles..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </InputGroup>
                      </Col>
                      <Col md={4}>
                        <Form.Control
                          as="select"
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                        >
                          <option value="">All Statuses</option>
                          <option value="ACTIVE">Active</option>
                          <option value="MAINTENANCE">Maintenance</option>
                          <option value="DECOMMISSIONED">Decommissioned</option>
                        </Form.Control>
                      </Col>
                    </Row>

                    {ansiblesLoading ? (
                      <div className="text-center">
                        <Spinner animation="border" />
                        <div className="mt-2">Loading ansibles...</div>
                      </div>
                    ) : (
                      <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                        <Table striped bordered hover responsive variant="dark">
                          <thead
                            style={{
                              position: 'sticky',
                              top: 0,
                              zIndex: 100,
                              backgroundColor: '#343a40',
                            }}
                          >
                            <tr>
                              <th style={{ borderTop: 'none', width: '80px' }}>
                                #
                              </th>
                              <th style={{ borderTop: 'none', width: '120px' }}>
                                Name
                              </th>
                              <th style={{ borderTop: 'none', width: '100px' }}>
                                Status
                              </th>
                              <th style={{ borderTop: 'none', width: '200px' }}>
                                Device Capacity
                              </th>
                              <th style={{ borderTop: 'none', width: '120px' }}>
                                Max Capacity
                              </th>
                              <th style={{ borderTop: 'none', width: '120px' }}>
                                Available
                              </th>
                              <th style={{ borderTop: 'none', width: '100px' }}>
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {ansiblesData?.data?.map((ansible, index) => (
                              <tr
                                key={ansible.id}
                                style={{ cursor: 'pointer' }}
                                onClick={() => handleAnsibleClick(ansible)}
                              >
                                <td>{(page - 1) * 20 + index + 1}</td>
                                <td>
                                  <Link
                                    to={`/inventory/terminals/${terminalId}/ansibles/${ansible.id}`}
                                    style={{
                                      color: '#4da6ff',
                                      textDecoration: 'underline',
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {ansible.name}
                                  </Link>
                                </td>
                                <td>
                                  <span
                                    className={`badge bg-${getStatusVariant(
                                      ansible.status
                                    )}`}
                                  >
                                    {ansible.status}
                                  </span>
                                </td>
                                <td>
                                  <div>
                                    <strong>Mac Minis:</strong>{' '}
                                    {ansible.currentMacMinis}/
                                    {ansible.maxMacMinis} (Available:{' '}
                                    {ansible.maxMacMinis -
                                      ansible.currentMacMinis}
                                    )
                                  </div>
                                  <div>
                                    <strong>Phones:</strong>{' '}
                                    {ansible.currentPhysicalPhones}/
                                    {ansible.maxPhysicalPhones} (Available:{' '}
                                    {ansible.maxPhysicalPhones -
                                      ansible.currentPhysicalPhones}
                                    )
                                  </div>
                                </td>
                                <td>
                                  <div>Mac: {ansible.maxMacMinis}</div>
                                  <div>Phone: {ansible.maxPhysicalPhones}</div>
                                </td>
                                <td>
                                  <div>
                                    Mac:{' '}
                                    {ansible.maxMacMinis -
                                      ansible.currentMacMinis}
                                  </div>
                                  <div>
                                    Phone:{' '}
                                    {ansible.maxPhysicalPhones -
                                      ansible.currentPhysicalPhones}
                                  </div>
                                </td>
                                <td onClick={(e) => e.stopPropagation()}>
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => handleAnsibleClick(ansible)}
                                  >
                                    View Slots
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}

                    {ansiblesData?.data?.length === 0 && (
                      <div className="text-center text-muted py-4">
                        No ansibles found matching your criteria
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Received Mac Minis Tab */}
              <Tab.Pane eventKey="received-mac-minis">
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      Received Mac Minis ({receivedMacMinisCount || 0})
                    </h5>
                    {macMiniSelection.hasSelection && (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={handleAssignMacMinis}
                      >
                        Assign Selected ({macMiniSelection.selectedCount})
                      </Button>
                    )}
                  </Card.Header>
                  <Card.Body>
                    {macMinisLoading ? (
                      <div className="text-center">
                        <Spinner animation="border" />
                        <div className="mt-2">
                          Loading received Mac Minis...
                        </div>
                      </div>
                    ) : receivedMacMinis?.data?.length === 0 ? (
                      <div className="text-center text-muted py-4">
                        No received Mac Minis found for this terminal
                      </div>
                    ) : (
                      <>
                        {availableMacMinis.length > 0 && (
                          <div className="mb-3">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() =>
                                macMiniSelection.selectAll(availableMacMinis)
                              }
                              className="me-2"
                            >
                              Select All
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => macMiniSelection.selectNone()}
                            >
                              Select None
                            </Button>
                          </div>
                        )}
                        <Table striped bordered hover responsive variant="dark">
                          <thead>
                            <tr>
                              <th style={{ width: '50px' }}>
                                <Form.Check
                                  type="checkbox"
                                  checked={
                                    macMiniSelection.selectedCount ===
                                      availableMacMinis.length &&
                                    availableMacMinis.length > 0
                                  }
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      macMiniSelection.selectAll(
                                        availableMacMinis
                                      )
                                    } else {
                                      macMiniSelection.selectNone()
                                    }
                                  }}
                                />
                              </th>
                              <th>Name</th>
                              <th>Model</th>
                              <th>Serial Number</th>
                              <th>MAC Address</th>
                              <th>Status</th>
                              <th>Created At</th>
                            </tr>
                          </thead>
                          <tbody>
                            {receivedMacMinis?.data?.map((macMini) => {
                              const deviceForSelection = {
                                id: macMini.id,
                                name: macMini.name,
                                type: 'mac-mini' as const,
                              }
                              return (
                                <tr key={macMini.id}>
                                  <td>
                                    <Form.Check
                                      type="checkbox"
                                      checked={macMiniSelection.isSelected(
                                        macMini.id
                                      )}
                                      onChange={() =>
                                        macMiniSelection.toggleDevice(
                                          deviceForSelection
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    <Link
                                      to={`/inventory/terminals/${terminalId}/mac-minis/${macMini.id}`}
                                      style={{
                                        color: '#4da6ff',
                                        textDecoration: 'underline',
                                      }}
                                    >
                                      {macMini.name}
                                    </Link>
                                  </td>
                                  <td>{macMini.model}</td>
                                  <td>{macMini.serialNumber}</td>
                                  <td>{macMini.macAddress}</td>
                                  <td>
                                    <span className="badge bg-warning">
                                      {macMini.status}
                                    </span>
                                  </td>
                                  <td>
                                    {new Date(
                                      macMini.createdAt
                                    ).toLocaleDateString()}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </Table>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Received Physical Phones Tab */}
              <Tab.Pane eventKey="received-phones">
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      Received Physical Phones ({receivedPhonesCount || 0})
                    </h5>
                    {phoneSelection.hasSelection && (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={handleAssignSelectedDevices}
                      >
                        Assign Selected ({phoneSelection.selectedCount})
                      </Button>
                    )}
                  </Card.Header>
                  <Card.Body>
                    {phonesLoading ? (
                      <div className="text-center">
                        <Spinner animation="border" />
                        <div className="mt-2">Loading received phones...</div>
                      </div>
                    ) : receivedPhones?.data?.length === 0 ? (
                      <div className="text-center text-muted py-4">
                        No received physical phones found for this terminal
                      </div>
                    ) : (
                      <>
                        {availablePhones.length > 0 && (
                          <div className="mb-3">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() =>
                                phoneSelection.selectAll(availablePhones)
                              }
                              className="me-2"
                            >
                              Select All
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => phoneSelection.selectNone()}
                            >
                              Select None
                            </Button>
                          </div>
                        )}
                        <Table striped bordered hover responsive variant="dark">
                          <thead>
                            <tr>
                              <th style={{ width: '50px' }}>
                                <Form.Check
                                  type="checkbox"
                                  checked={
                                    phoneSelection.selectedCount ===
                                      availablePhones.length &&
                                    availablePhones.length > 0
                                  }
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      phoneSelection.selectAll(availablePhones)
                                    } else {
                                      phoneSelection.selectNone()
                                    }
                                  }}
                                />
                              </th>
                              <th>Name</th>
                              <th>Device Model</th>
                              <th>Serial Number</th>
                              <th>IMEI</th>
                              <th>Status</th>
                              <th>Created At</th>
                            </tr>
                          </thead>
                          <tbody>
                            {receivedPhones?.data?.map((phone) => {
                              const deviceForSelection = {
                                id: phone.id,
                                name: phone.name,
                                type: 'physical-phone' as const,
                              }
                              return (
                                <tr key={phone.id}>
                                  <td>
                                    <Form.Check
                                      type="checkbox"
                                      checked={phoneSelection.isSelected(
                                        phone.id
                                      )}
                                      onChange={() =>
                                        phoneSelection.toggleDevice(
                                          deviceForSelection
                                        )
                                      }
                                    />
                                  </td>
                                  <td>
                                    <Link
                                      to={`/inventory/terminals/${terminalId}/physical-phones/${phone.id}`}
                                      style={{
                                        color: '#4da6ff',
                                        textDecoration: 'underline',
                                      }}
                                    >
                                      {phone.name}
                                    </Link>
                                  </td>
                                  <td>{phone.deviceModel}</td>
                                  <td>{phone.serialNumber}</td>
                                  <td>{phone.imei || 'N/A'}</td>
                                  <td>
                                    <span className="badge bg-warning">
                                      {phone.status}
                                    </span>
                                  </td>
                                  <td>
                                    {new Date(
                                      phone.createdAt
                                    ).toLocaleDateString()}
                                  </td>
                                </tr>
                              )
                            })}
                          </tbody>
                        </Table>
                      </>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Apple IDs Tab */}
              <Tab.Pane eventKey="apple-id-configs">
                <Card>
                  <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">
                      Apple IDs ({appleIdConfigsData?.meta?.total || 0})
                    </h5>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => setShowCreateAppleIdModal(true)}
                    >
                      Add Apple ID
                    </Button>
                  </Card.Header>
                  <Card.Body>
                    <Alert variant="info">
                      <strong>Note:</strong> This shows available (unassigned)
                      Apple IDs only. Once assigned to a phone number, they will
                      no longer appear in this list. Apple IDs are global
                      resources and not specific to this terminal.
                    </Alert>

                    {appleIdConfigsLoading ? (
                      <div className="text-center">
                        <Spinner animation="border" />
                        <div className="mt-2">Loading Apple IDs...</div>
                      </div>
                    ) : appleIdConfigsData?.data?.length === 0 ? (
                      <div className="text-center text-muted py-4">
                        No available Apple IDs found. All Apple IDs may be
                        assigned, or you can create a new one.
                      </div>
                    ) : (
                      <Table striped bordered hover responsive variant="dark">
                        <thead>
                          <tr>
                            <th>Apple ID Email</th>
                            <th>iMessage</th>
                            <th>FaceTime</th>
                            <th>SMS Forwarding</th>
                            <th>Call Forwarding</th>
                            <th>Created At</th>
                          </tr>
                        </thead>
                        <tbody>
                          {appleIdConfigsData?.data?.map((config) => (
                            <tr key={config.id}>
                              <td>{config.appleIdEmail || 'N/A'}</td>
                              <td>
                                <span
                                  className={`badge bg-${
                                    config.iMessageEnabled
                                      ? 'success'
                                      : 'secondary'
                                  }`}
                                >
                                  {config.iMessageEnabled
                                    ? 'Enabled'
                                    : 'Disabled'}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`badge bg-${
                                    config.facetimeEnabled
                                      ? 'success'
                                      : 'secondary'
                                  }`}
                                >
                                  {config.facetimeEnabled
                                    ? 'Enabled'
                                    : 'Disabled'}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`badge bg-${
                                    config.smsForwardingEnabled
                                      ? 'success'
                                      : 'secondary'
                                  }`}
                                >
                                  {config.smsForwardingEnabled
                                    ? 'Enabled'
                                    : 'Disabled'}
                                </span>
                              </td>
                              <td>
                                <span
                                  className={`badge bg-${
                                    config.callForwardingEnabled
                                      ? 'success'
                                      : 'secondary'
                                  }`}
                                >
                                  {config.callForwardingEnabled
                                    ? 'Enabled'
                                    : 'Disabled'}
                                </span>
                              </td>
                              <td>
                                {new Date(
                                  config.createdAt
                                ).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </Card.Body>
                </Card>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>

          {/* Device Assignment Modal - for mixed or phone-only selections */}
          <DeviceAssignmentModal
            show={showAssignmentModal}
            onHide={() => setShowAssignmentModal(false)}
            devices={allSelectedDevices}
            terminalId={terminalId}
            terminalName={terminal?.name}
            onSuccess={handleAssignmentSuccess}
          />

          {/* Mac Mini Assignment Modal - with naming convention */}
          <MacMiniAssignmentModal
            show={showMacMiniAssignmentModal}
            onHide={() => setShowMacMiniAssignmentModal(false)}
            macMinis={
              macMiniSelection.selectedDevices
                .map((selectedDevice) =>
                  receivedMacMinis?.data?.find(
                    (macMini) => macMini.id === selectedDevice.id
                  )
                )
                .filter(Boolean) as PnsMacMini[]
            }
            terminalId={terminalId}
            terminalName={terminal?.name}
            onSuccess={handleMacMiniAssignmentSuccess}
          />

          {/* Create Apple ID Modal */}
          <CreateAppleIdModal
            show={showCreateAppleIdModal}
            onHide={() => setShowCreateAppleIdModal(false)}
          />
        </>
      )}
    </Container>
  )
}

export default InventoryTerminalDetailPage
